import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  LayoutDashboard, Users, Clock, AlertCircle, CheckCircle2, XCircle, RefreshCw, TrendingUp, TrendingDown, Bell, Filter, Search, Eye, EyeOff, Edit, Save, Plus, Trash2, Info, HelpCircle, ExternalLink, Copy, Globe, Mail, Phone, MessageSquare, Heart, Award, Target, Timer, Brain, Route, Zap, MapPin, FileText, File, FileVideo, FileAudio, FileImage, FileSpreadsheet, FilePdf, FileZip, FileCheck, FileX, FilePlus, FileMinus, FileEdit, FileSearch, FileSlash, FileWarning, FileQuestion, FileHeart, FileClock, FileUp, FileDown, FileLeft, FileRight, FileBarChart, FilePieChart, FileLineChart, FileScissors, FileType, FileSymlink, FileStack, FileTree, FileBinary, FileCog, FileSettings, FileLock, FileUnlock, FileKey, FileSignature, FileVerified, FileBadge, FileTag, FileLabel, FileBookmark, FileStar, FileFlag, FileAlert, FileInfo, FileHelp, FileMessage, FileComment, FileChat, FileMail, FilePhone, Activity, BarChart3, PieChart, LineChart, ScrollText, Database, HardDrive, Cpu, MemoryStick, Network, Server, Wrench, Shield, Archive, Download, Upload, List, Play, Pause, Stop, UserCheck, UserX, UserPlus, UserMinus, ArrowRight, ArrowLeft, ArrowUp, ArrowDown, RotateCcw, Settings, Target as TargetIcon, Zap as ZapIcon, Brain as BrainIcon, Route as RouteIcon
} from 'lucide-react';
import { AgentService } from '../../services/AgentService';

const TicketAssignment = () => {
  const [assignmentMetrics, setAssignmentMetrics] = useState(null);
  const [unassignedTickets, setUnassignedTickets] = useState([]);
  const [assignmentRules, setAssignmentRules] = useState([]);
  const [agentWorkload, setAgentWorkload] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [assignmentMode, setAssignmentMode] = useState('manual');
  const loadAssignmentDataRef = useRef(null);

  const loadAssignmentData = useCallback(async () => {
    try {
      setLoading(true);
      const [metricsResponse, ticketsResponse, rulesResponse, workloadResponse] = await Promise.all([
        AgentService.getAssignmentMetrics(),
        AgentService.getUnassignedTickets(),
        AgentService.getAssignmentRules(),
        AgentService.getAgentWorkload()
      ]);

      if (metricsResponse.success) setAssignmentMetrics(metricsResponse.data);
      if (ticketsResponse.success) setUnassignedTickets(ticketsResponse.data);
      if (rulesResponse.success) setAssignmentRules(rulesResponse.data);
      if (workloadResponse.success) setAgentWorkload(workloadResponse.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssignmentData();
  }, [loadAssignmentData]);

  loadAssignmentDataRef.current = loadAssignmentData;

  useEffect(() => {
    const onRealtimeRefresh = () => {
      loadAssignmentDataRef.current?.();
    };
    window.addEventListener('hd:queue-realtime-refresh', onRealtimeRefresh);
    return () => window.removeEventListener('hd:queue-realtime-refresh', onRealtimeRefresh);
  }, []);

  const handleAutoAssign = async () => {
    try {
      const response = await AgentService.autoAssignTickets();
      if (response.success) {
        await loadAssignmentData();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleManualAssign = async (ticketIds, agentId) => {
    try {
      const response = await AgentService.assignTickets(ticketIds, agentId);
      if (response.success) {
        await loadAssignmentData();
        setSelectedTickets([]);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleBulkAssign = async () => {
    if (selectedTickets.length === 0 || !selectedAgent) return;
    
    try {
      const response = await AgentService.assignTickets(selectedTickets, selectedAgent);
      if (response.success) {
        await loadAssignmentData();
        setSelectedTickets([]);
        setSelectedAgent(null);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'urgent': return 'bg-red-800 text-red-50';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-purple-100 text-purple-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <XCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading assignment data</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Advanced Ticket Assignment</h1>
          <p className="text-gray-600">Manage ticket assignments and workload distribution</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleAutoAssign}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <ZapIcon className="w-4 h-4 mr-2" />
            Auto Assign
          </button>
          <button
            onClick={loadAssignmentData}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: LayoutDashboard },
            { id: 'tickets', name: 'Unassigned Tickets', icon: FileText },
            { id: 'agents', name: 'Agent Workload', icon: Users },
            { id: 'rules', name: 'Assignment Rules', icon: Settings }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && assignmentMetrics && (
        <div className="space-y-6">
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {assignmentMetrics.overviewMetrics.map((metric, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                    <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                  </div>
                  <div className={`p-3 rounded-full ${
                    metric.status === 'good' ? 'bg-green-100' :
                    metric.status === 'warning' ? 'bg-yellow-100' :
                    'bg-red-100'
                  }`}>
                    {metric.status === 'good' ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : metric.status === 'warning' ? (
                      <AlertCircle className="w-6 h-6 text-yellow-600" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-600" />
                    )}
                  </div>
                </div>
                <div className="mt-4 flex items-center">
                  {metric.trend === 'up' ? (
                    <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                  )}
                  <span className={`text-sm ${
                    metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {metric.change}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Assignment Statistics */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Assignment Statistics</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {assignmentMetrics.assignmentStats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 ${
                      stat.type === 'auto' ? 'bg-blue-100' :
                      stat.type === 'manual' ? 'bg-green-100' :
                      'bg-purple-100'
                    }`}>
                      {stat.type === 'auto' ? (
                        <ZapIcon className="w-8 h-8 text-blue-600" />
                      ) : stat.type === 'manual' ? (
                        <UserCheck className="w-8 h-8 text-green-600" />
                      ) : (
                        <RouteIcon className="w-8 h-8 text-purple-600" />
                      )}
                    </div>
                    <h4 className="text-lg font-medium text-gray-900">{stat.name}</h4>
                    <p className="text-sm text-gray-600">{stat.count} tickets</p>
                    <p className="text-sm font-medium text-gray-900 mt-1">{stat.percentage}%</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Assignments */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Assignments</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {assignmentMetrics.recentAssignments.map((assignment, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className={`p-2 rounded-full ${
                        assignment.method === 'auto' ? 'bg-blue-100' : 'bg-green-100'
                      }`}>
                        {assignment.method === 'auto' ? (
                          <ZapIcon className="w-4 h-4 text-blue-600" />
                        ) : (
                          <UserCheck className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">Ticket #{assignment.ticketId}</p>
                        <p className="text-sm text-gray-600">Assigned to {assignment.agentName}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-900">{assignment.timestamp}</p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(assignment.priority)}`}>
                        {assignment.priority}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unassigned Tickets Tab */}
      {activeTab === 'tickets' && (
        <div className="space-y-6">
          {/* Bulk Assignment Controls */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <select
                  value={selectedAgent || ''}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Agent</option>
                  {agentWorkload.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name} ({agent.currentTickets} tickets)
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleBulkAssign}
                  disabled={selectedTickets.length === 0 || !selectedAgent}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <UserCheck className="w-4 h-4 mr-2" />
                  Assign Selected ({selectedTickets.length})
                </button>
              </div>
              <div className="text-sm text-gray-600">
                {selectedTickets.length} tickets selected
              </div>
            </div>
          </div>

          {/* Tickets List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Unassigned Tickets</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTickets(unassignedTickets.map(t => t.id));
                          } else {
                            setSelectedTickets([]);
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {unassignedTickets.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedTickets.includes(ticket.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTickets([...selectedTickets, ticket.id]);
                            } else {
                              setSelectedTickets(selectedTickets.filter(id => id !== ticket.id));
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{ticket.id}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-md truncate">
                        {ticket.subject}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                          {ticket.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {ticket.createdAt}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedTickets([ticket.id]);
                            // Show agent selection modal or dropdown
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <UserCheck className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Agent Workload Tab */}
      {activeTab === 'agents' && (
        <div className="space-y-6">
          {/* Agent Workload Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {agentWorkload.map((agent) => (
              <div key={agent.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-lg font-medium text-gray-900">{agent.name}</h3>
                        <p className="text-sm text-gray-600">{agent.department}</p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      agent.status === 'available' ? 'bg-green-100 text-green-800' :
                      agent.status === 'busy' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {agent.status}
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Current Tickets</span>
                      <span className="text-sm font-medium text-gray-900">{agent.currentTickets}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Max Capacity</span>
                      <span className="text-sm font-medium text-gray-900">{agent.maxCapacity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Utilization</span>
                      <span className="text-sm font-medium text-gray-900">{agent.utilization}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          agent.utilization < 70 ? 'bg-green-500' :
                          agent.utilization < 90 ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${agent.utilization}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Avg Response Time</span>
                      <span className="font-medium text-gray-900">{agent.avgResponseTime}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-gray-600">Resolution Rate</span>
                      <span className="font-medium text-gray-900">{agent.resolutionRate}%</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assignment Rules Tab */}
      {activeTab === 'rules' && (
        <div className="space-y-6">
          {/* Assignment Rules List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Assignment Rules</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {assignmentRules.map((rule) => (
                  <div key={rule.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className={`p-2 rounded-full ${
                        rule.enabled ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        {rule.enabled ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : (
                          <XCircle className="w-4 h-4 text-gray-600" />
                        )}
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-900">{rule.name}</h4>
                        <p className="text-sm text-gray-600">{rule.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        rule.priority === 'high' ? 'bg-red-100 text-red-800' :
                        rule.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {rule.priority}
                      </span>
                      <button className="text-gray-600 hover:text-gray-900">
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketAssignment;
